import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ApiError, fetchMemberCheckInStatus, memberSelfCheckIn, type MemberCheckInStatus } from "../api/client";
import { RequireAuth } from "../components/RequireAuth";

function MemberCheckInInner() {
  const [status, setStatus] = useState<MemberCheckInStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ name: string; already: boolean } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setStatus(await fetchMemberCheckInStatus());
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not load check-in");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function checkIn() {
    setBusy(true);
    setError(null);
    try {
      const result = await memberSelfCheckIn();
      setDone({ name: result.name, already: result.alreadyCheckedIn });
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not check you in");
    } finally {
      setBusy(false);
    }
  }

  const ongoing = status?.event?.phase === "ongoing" ? status.event : null;

  return (
    <div className="member-checkin">
      <div className="member-checkin-inner">
        <div className="sec-eyebrow">
          <em>tonight</em>
        </div>
        <h1 className="member-checkin-title">Check in</h1>

        {loading ? (
          <p className="member-checkin-lede">Looking up tonight’s overnight…</p>
        ) : done ? (
          <div className="kiosk-success" role="status" style={{ position: "relative", inset: "auto" }}>
            <div className="kiosk-success-inner">
              <div className="sec-eyebrow">
                <em>{done.already ? "already here" : "you’re in"}</em>
              </div>
              <h2 className="kiosk-success-title">
                {done.already ? "You’re already in" : `Welcome, ${done.name}`}
              </h2>
              <p className="kiosk-success-lede">
                {done.already
                  ? `${done.name}, you’re already checked in for tonight. Enjoy the gathering.`
                  : "You’re checked in. A host can help if you need anything."}
              </p>
              <Link to="/" className="btn btn-primary">
                Back home
              </Link>
            </div>
          </div>
        ) : (
          <>
            {error && (
              <p className="member-checkin-error" role="alert">
                {error}
              </p>
            )}

            {!ongoing ? (
              <>
                <p className="member-checkin-lede">
                  {status?.event
                    ? status.event.phase === "upcoming"
                      ? `Next up: ${status.event.title?.trim() || "Overnight"} — check-in opens when it starts.`
                      : "There isn’t an overnight happening right now."
                    : "No overnight is on the calendar yet. Ask a host if you’re at the door."}
                </p>
                <Link to="/" className="btn btn-ghost">
                  Back home
                </Link>
              </>
            ) : (
              <>
                <p className="member-checkin-lede">
                  <strong>{ongoing.title?.trim() || "Tonight’s overnight"}</strong>
                  <br />
                  Checking in as{" "}
                  <strong>{status?.contact.name}</strong>
                  {status?.contact.email ? ` · ${status.contact.email}` : ""}
                </p>
                {status?.alreadyCheckedIn ? (
                  <>
                    <p className="member-checkin-lede">You’re already on tonight’s list.</p>
                    <button
                      type="button"
                      className="btn btn-primary"
                      disabled={busy}
                      onClick={() => void checkIn()}
                    >
                      Show welcome
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={busy}
                    onClick={() => void checkIn()}
                  >
                    {busy ? "Checking in…" : "Check me in"}
                  </button>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export function MemberCheckInPage() {
  return (
    <RequireAuth>
      <MemberCheckInInner />
    </RequireAuth>
  );
}
